import Head from 'next/head'
import { Box, Button } from 'theme-ui'
import { JSX, useState } from 'react'
import { TabPanel } from 'react-tabs'

import Default from 'src/layouts/Default'
import { PageTabs, PageTab, PageTabList } from 'src/components/pageTabs/PageTabs'
import Type from 'src/components/gel/Type'
import MinimalHeader from 'src/components/pageHeader/MinimalHeader'
import Grid, { GridCard, GridProps } from 'src/components/common/grid/Grid'
import Icon from 'src/components/gel/Icon'

const AdFormatsListPage = ({ cards }: { cards: GridCard[] }) => {
  const formatTypes = ['All Ad Formats', 'Digital Display', 'Audio', 'Social', 'Video']

  const getFormatCards = (type: string, props?: Partial<GridProps>) => {
    const formatCards = cards.filter(c => c.filterCategories.includes(type))

    return formatCards.length > 0 ? (
      <Grid
        title={type}
        cards={formatCards}
        styleOptions={{
          addBottomBorder: false
        }}
        {...props}
      />
    ) : null
  }

  return (
    cards.length > 0 && (
      <Default>
        <Head>
          <title>{'Ad Formats List - BBC Studios Advertising'}</title>
          <meta
            name="description"
            content="BBC Studios creates and distributes globally acclaimed content that shapes culture and drives conversation. For advertisers, it's a powerful opportunity to connect with audiences in the moments that matter — where influence is built, loyalty is earned, and brands make a lasting impact."
          />
          <meta
            name="keywords"
            content="BBC Studios, Advertising, Media, Content Distribution, Brand Partnerships"
          />
          <link rel="icon" type="image/x-icon" href="/favicon.ico" />
        </Head>
        <Box as="main">
          <MinimalHeader
            breadcrumbs={[{ name: 'Ad Formats List Page' }]}
            title={'Ad Formats'}
            backgroundColor={'background.primary'}
          />
          <DesktopTabView formatTypes={formatTypes} getFormatCards={getFormatCards} />
          <MobileTabView formatTypes={formatTypes} getFormatCards={getFormatCards} />
        </Box>
      </Default>
    )
  )
}

const MobileTabView = ({
  formatTypes,
  getFormatCards
}: {
  formatTypes: string[]
  getFormatCards: (type: string, props?: Partial<GridProps>) => JSX.Element | null
}) => {
  const [tabIndex, setTabIndex] = useState<number>(0)
  const [toggleMobileOpen, setToggleMobileOpen] = useState(false)

  const handleSelect = (index: number | any) => {
    if (typeof index !== 'number' || index === tabIndex) return

    setTabIndex(index)
  }

  return (
    <Box sx={{ display: ['inherit', 'inherit', 'none', 'none'] }}>
      <Box
        sx={{
          display: 'block',
          mx: [4],
          mt: [6],
          border: '1px solid #E0E0E0',
          textAlign: 'center'
        }}
      >
        <Button
          variant="buttons.tertiary"
          onClick={() => {
            setToggleMobileOpen(!toggleMobileOpen)
          }}
          as="div"
          sx={{
            width: '100%',
            p: 2
          }}
        >
          <Type variant="Labels" as="span">
            {formatTypes[tabIndex]}
          </Type>
          <Icon
            variant={'GelIconArrowDown'}
            sx={{
              ml: 2,
              mt: '2px',
              verticalAlign: 'top',
              display: 'inline-flex'
            }}
            width={'20px'}
            height={'20px'}
          />
        </Button>
        {toggleMobileOpen ? (
          <Box
            sx={{
              width: '100%',
              position: 'relative',
              backgroundColor: 'background.quaternary',
              borderBottom: 'tertiaryThin',
              borderTop: 'tertiaryThin'
            }}
          >
            {formatTypes.map((type, idx) => {
              return type !== formatTypes[tabIndex] ? (
                <Button
                  key={type}
                  variant="buttons.tertiary"
                  onClick={() => {
                    setToggleMobileOpen(!toggleMobileOpen)
                    handleSelect(idx)
                  }}
                  sx={{
                    width: '100%'
                  }}
                >
                  <Type variant="Body" as="span">
                    {type}
                  </Type>
                </Button>
              ) : null
            })}
          </Box>
        ) : null}
      </Box>
      {tabIndex === 0
        ? formatTypes
            .slice(1)
            .map((type, idx) =>
              getFormatCards(type, { styleOptions: { addBottomBorder: idx < 3 } })
            )
        : getFormatCards(formatTypes[tabIndex])}
    </Box>
  )
}

const DesktopTabView = ({
  formatTypes,
  getFormatCards
}: {
  formatTypes: string[]
  getFormatCards: (type: string, props?: Partial<GridProps>) => JSX.Element | null
}) => {
  const [tabIndex, setTabIndex] = useState<number>(0)

  const handleSelect = (index: number | any) => {
    if (typeof index !== 'number' || index === tabIndex) return

    setTabIndex(index)
  }

  return (
    <Box variant="layout.wideContainer" sx={{ display: ['none', 'none', 'inherit', 'inherit'] }}>
      <PageTabs
        variant={'pageTabs.primary'}
        selectedIndex={tabIndex}
        onSelect={idx => handleSelect(idx)}
      >
        <PageTabList>
          {formatTypes.map(type => (
            <PageTab disabled={false} key={`page-tab--${type}`}>
              <Type variant="Labels" as="span">
                {type}
              </Type>
            </PageTab>
          ))}
        </PageTabList>
        <TabPanel>
          {formatTypes
            .slice(1)
            .map((type, idx) =>
              getFormatCards(type, { styleOptions: { addBottomBorder: idx < 3 } })
            )}
        </TabPanel>
        {formatTypes.slice(1).map(type => (
          <TabPanel key={type}>{getFormatCards(type)}</TabPanel>
        ))}
      </PageTabs>
    </Box>
  )
}

export default AdFormatsListPage
